import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { I18nUtil } from "@utils/i18n";
import { Locale } from "@utils/i18n.locale";
import { OpenDataTypeEnum, ThemeItem } from "../features/api/openData";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { QUERY_KEYS } from "@config/constant";

interface UseAIInsightDetailOptions {
  themeSlug?: string;
  themeId?: string;
  market?: string;
  type: OpenDataTypeEnum;
  enabled?: boolean;
  locale?: Locale;
}

export const useAIInsightDetail = (
  options: UseAIInsightDetailOptions,
): UseQueryResult<ThemeItem[] | null> => {
  const { openDataApi } = useOspApiClientProvider();
  const locale = I18nUtil.getCurrentLocale();
  const { enabled = true, themeSlug, themeId, market, type } = options;

  const result = useQuery<ThemeItem[] | null>({
    queryKey: [
      QUERY_KEYS.THEME_DETAIL,
      { themeSlug, themeId, market, type, locale: locale },
    ],
    enabled: !!openDataApi && enabled && (!!themeSlug || !!themeId) && !!type,
    queryFn: async () => {
      if (!openDataApi?.themes?.getThemes) {
        throw new Error("Themes API not available");
      }
      const language = I18nUtil.getBackgroundLanguageKey(locale);
      const themeIds = themeId ? themeId.split(",") : undefined;
      const themeSlugs = themeSlug ? themeSlug.split(",") : undefined;
      const limit =
        (themeIds && themeIds.length) || (themeSlugs && themeSlugs.length) || 1;
      const response = await openDataApi.themes.getThemes({
        slugs: themeSlugs,
        ids: themeIds,
        market,
        language: language,
        offset: 0,
        limit,
        type,
      });
      return response?.data?.data?.rows || null;
    },
  });

  return result;
};
